import { useMemo, useState } from 'react';
import { componentSizes } from '@/theme/componentSizes';
import { Box } from './Box';
import { AddIcon, RemoveIcon } from './icons';
import { Text } from './Text';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ChangeEvent, ReactElement, ReactNode } from 'react';

export type NumberSpinnerProps = {
  label?: ReactNode;
  value?: string | number | null;
  defaultValue?: number;
  onChange?: (next: string) => void;
  min?: number;
  max?: number;
  step?: number;
  size?: 'small' | 'medium';
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  sx?: SxProps<Theme>;
};

const parseValue = (value: string | number | null | undefined): number | null => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clampValue = (value: number, min?: number, max?: number): number => {
  const minClamped = min === undefined ? value : Math.max(min, value);
  return max === undefined ? minClamped : Math.min(max, minClamped);
};

const HALF_STEP_INPUT_PATTERN = /^\d+(\.5?)?$/;

export const NumberSpinner = ({
  label,
  value,
  defaultValue,
  onChange,
  min,
  max,
  step = 0.5,
  size = 'medium',
  disabled = false,
  error = false,
  placeholder,
  sx,
}: NumberSpinnerProps): ReactElement => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<string>(
    defaultValue === undefined ? '' : String(defaultValue),
  );

  const resolvedValue = isControlled ? (value === null ? '' : String(value)) : internalValue;
  const resolvedNumber = useMemo(() => parseValue(resolvedValue), [resolvedValue]);
  const inputHeight = size === 'small' ? 40 : 56;

  const applyValue = (next: string): void => {
    if (!isControlled) setInternalValue(next);
    onChange?.(next);
  };

  const applyStep = (direction: 1 | -1): void => {
    const start = resolvedNumber ?? min ?? 0;
    const next = clampValue(start + direction * step, min, max);
    applyValue(String(next));
  };

  const canDecrement = !(disabled || (min !== undefined && (resolvedNumber ?? min) <= min));
  const canIncrement = !(disabled || (max !== undefined && (resolvedNumber ?? max) >= max));

  return (
    <Box sx={sx}>
      <Box sx={{ display: 'grid', gap: '4px' }}>
        {label ? <Text role="body">{label}</Text> : null}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            alignItems: 'stretch',
            minHeight: inputHeight,
            border: 1,
            borderColor: error ? 'error.main' : 'divider',
            borderRadius: '4px',
            backgroundColor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <Box
            component="button"
            type="button"
            aria-label="Decrease value"
            disabled={!canDecrement}
            sx={{
              appearance: 'none',
              borderRadius: 0,
              borderTopLeftRadius: '4px',
              borderBottomLeftRadius: '4px',
              border: 0,
              margin: 0,
              width: componentSizes.forms.numberSpinnerStepperWidth,
              borderRight: 1,
              borderColor: 'divider',
              backgroundColor: 'transparent',
              color: 'text.secondary',
              display: 'grid',
              placeItems: 'center',
              cursor: canDecrement ? 'pointer' : 'not-allowed',
              '&:disabled': { opacity: 0.5 },
            }}
            onClick={() => {
              applyStep(-1);
            }}
          >
            <RemoveIcon sx={{ fontSize: 18 }} />
          </Box>

          <Box
            component="input"
            type="number"
            value={resolvedValue}
            disabled={disabled}
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
            inputMode="decimal"
            sx={{
              width: '100%',
              border: 0,
              outline: 0,
              textAlign: 'center',
              background: 'transparent',
              color: 'text.primary',
              typography: 'body1',
              px: '12px',
              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { display: 'none' },
              MozAppearance: 'textfield',
            }}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const next = event.target.value;
              if (next === '' || HALF_STEP_INPUT_PATTERN.test(next)) {
                applyValue(next);
              }
            }}
          />

          <Box
            component="button"
            type="button"
            aria-label="Increase value"
            disabled={!canIncrement}
            sx={{
              appearance: 'none',
              borderRadius: 0,
              borderTopRightRadius: '4px',
              borderBottomRightRadius: '4px',
              border: 0,
              margin: 0,
              width: componentSizes.forms.numberSpinnerStepperWidth,
              borderLeft: 1,
              borderColor: 'divider',
              backgroundColor: 'transparent',
              color: 'text.secondary',
              display: 'grid',
              placeItems: 'center',
              cursor: canIncrement ? 'pointer' : 'not-allowed',
              '&:disabled': { opacity: 0.5 },
            }}
            onClick={() => {
              applyStep(1);
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export type NumberFieldProps = NumberSpinnerProps;
export const NumberField = NumberSpinner;
